/**
 * A hook to allow users to copy a shared link
 *
 * - Returns an `onCopy` callback which returns the event.
 * - Also returns a `didCopy` flag whose value is `true` for 2 seconds before reverting back to `false`
 *
 * @module
 */
import { useEffect, useState, useRef, useCallback } from 'react';
/**
 *
 * # useCopy
 *
 * Usage example:
 *
 * ```javascript
 * import useCopy from "@elevio/kb-kit/lib/hooks/useCopy";
 *
 * const MyFunction = () => {
 *   const { onCopy, didCopy } = useCopy();
 *   console.log(onCopy); // function(e: React.MouseEvent)
 *   console.log(didCopy); // boolean
 *
 *   return (
 *     <div>
 *       {didCopy ? <div className="copied-notice">Copied!</div> : null}
 *       <button className="copy-button" onClick={(e) => onCopy(e)}>
 *         Click me to copy share link
 *       </button>
 *     </div>
 *   );
 * };
 * ```
 */
function useCopy() {
    const [didCopy, setDidCopy] = useState(false);
    const timeoutRef = useRef(null);
    const onCopy = useCallback((e) => {
        // Need to prevent the click from flowing through to the parent
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setDidCopy(true);
        // Create dummy element to be able to access clipboard
        const dummy = document.createElement('input');
        const text = window.location.href;
        document.body.appendChild(dummy);
        dummy.value = text;
        dummy.select();
        document.execCommand('copy');
        document.body.removeChild(dummy);
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = window.setTimeout(() => setDidCopy(false), 2000);
    }, []);
    // same as componentWillUnmount
    useEffect(() => {
        return function () {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);
    return { onCopy, didCopy };
}
export default useCopy;
//# sourceMappingURL=useCopy.js.map